import { Button } from "./ui/button";
import { PageLink } from "./PageLink";
import { Card, CardContent } from "./ui/card";
import { FAQ } from "./FAQ";
import { HeroCarousel } from "./HeroCarousel";
import { siteConfig } from "../config/siteConfig";
import { industries, chipClass } from "../data/solutions";
import { platformTopics } from "../data/platform";

// Home teasers link to the Platform capability pages (product features live under Platform).
const capabilityTeasers = platformTopics.filter((t) =>
  ["agents", "squads", "rag", "integrations"].includes(t.slug)
);
import {
  Workflow,
  Bot,
  Users,
  ShieldCheck,
  Zap,
  CheckCircle,
  Globe,
  ArrowRight,
  Cloud,
  Server,
  Building2,
  Cpu,
  Lock,
  ScrollText,
  KeyRound,
  EyeOff,
} from "lucide-react";

interface TurfAIPageProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

const pillars = [
  {
    icon: Workflow,
    accent: "indigo",
    title: "Workflows",
    desc: "A visual canvas to design multi-step automations — triggers, branching, retries, and integrations, no glue code.",
  },
  {
    icon: Bot,
    accent: "teal",
    title: "Agents",
    desc: "ReAct-style agents that reason, call governed tools, and act — grounded in your knowledge with RAG.",
  },
  {
    icon: Users,
    accent: "purple",
    title: "Squads",
    desc: "Multi-agent teams that collaborate on complex work, with a defined coordination pattern and human sign-off.",
  },
  {
    icon: ShieldCheck,
    accent: "rose",
    title: "Governance",
    desc: "RBAC, audit trails, cost tracking, and Data Shield, which tokenises personal data before it reaches a model.",
  },
];

const deployments = [
  { icon: Cloud, title: "SaaS", desc: "Fully managed by Ariviti. Fastest to start." },
  { icon: Server, title: "BYOC", desc: "Run in your own cloud account (VPC)." },
  { icon: Building2, title: "On-prem", desc: "Inside your data centre. Runtime data stays there." },
  { icon: Cpu, title: "Edge", desc: "Close to the data, for low-latency work." },
];

export function TurfAIPage({ onNavigate, onNavigateToDemo }: TurfAIPageProps) {
  return (
    <div className="min-h-screen bg-white">
      <HeroCarousel onNavigate={onNavigate} onNavigateToDemo={onNavigateToDemo} />

      {/* Execution gap */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">The execution gap</p>
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-6">Most enterprise AI never reaches production</h2>
            <p className="text-[22px] leading-relaxed text-soft-black">
              Pilots stall because real work needs more than a model call — it needs orchestration
              across agents, people, and systems, with governance an enterprise can trust. TurfAI is
              the execution layer that closes that gap.
            </p>
          </div>
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Zap, label: "Trigger", desc: "Events, schedules, uploads, or API calls start the work." },
              { icon: Workflow, label: "Orchestrate", desc: "Agents and squads reason, call tools, and chain steps." },
              { icon: CheckCircle, label: "Human-in-the-loop", desc: "People review and approve where stakes are high." },
              { icon: Globe, label: "Act", desc: "Write back to your systems and deliver the outcome." },
            ].map((step) => (
              <Card key={step.label} className="p-4 sm:p-6 border-softblack-tint-20">
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-indigo-tint-20 rounded-xl flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-royal-indigo" />
                  </div>
                  <h3 className="font-semibold text-dark-indigo mb-1">{step.label}</h3>
                  <p className="text-sm text-soft-black">{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Four capability pillars */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">One platform. Four building blocks.</h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              What it takes to run AI in production, not just call a model.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {pillars.map((p) => (
              <Card key={p.title} className="p-6 bg-white transition-shadow border-softblack-tint-20">
                <CardContent className="p-0">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${chipClass(p.accent)}`}>
                    <p.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-[22px] font-semibold text-dark-indigo mb-2">{p.title}</h3>
                  <p className="text-sm text-soft-black">{p.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button variant="outline" className="border-indigo-tint-50 text-soft-black hover:bg-softblack-tint-20" asChild><PageLink page={"platform"} onNavigate={onNavigate}>
              See how it works <ArrowRight className="w-4 h-4 ml-2" />
            </PageLink></Button>
          </div>
        </div>
      </section>

      {/* Industry teasers */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Where we've done this before</h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              Domain-tuned workflows for the industries we know best.
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {industries.map((ind) => (
              <PageLink page={ind.pageId} onNavigate={onNavigate}
                key={ind.slug}
                className="text-left p-6 rounded-xl border border-softblack-tint-20 hover:border-indigo-tint-50 transition-all"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${chipClass(ind.accent)}`}>
                  <ind.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-dark-indigo mb-1">{ind.name}</h3>
                <p className="text-sm text-soft-black font-medium mb-3">
                  {ind.proof[0].metric} {ind.proof[0].label}
                </p>
                <span className="text-sm font-medium text-royal-indigo inline-flex items-center">
                  Explore <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </PageLink>
            ))}
          </div>
        </div>
      </section>

      {/* Capability teasers */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Or start from a capability</h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              The building blocks teams ship most — fully editable on the canvas.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {capabilityTeasers.map((t) => (
              <PageLink page={t.pageId} onNavigate={onNavigate}
                key={t.slug}
                className="text-left p-6 rounded-xl bg-white border border-softblack-tint-20 hover:border-indigo-tint-50 transition-all"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${chipClass(t.accent)}`}>
                  <t.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-dark-indigo mb-2">{t.name}</h3>
                <span className="text-sm font-medium text-royal-indigo inline-flex items-center">
                  Explore <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </PageLink>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment options */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Deploy it where your data lives</h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              Same platform, your choice of boundary — from fully managed to fully on-prem.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {deployments.map((d) => (
              <Card key={d.title} className="p-4 sm:p-6 text-center border-softblack-tint-20">
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-indigo-tint-20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <d.icon className="w-6 h-6 text-royal-indigo" />
                  </div>
                  <h3 className="font-semibold text-dark-indigo mb-1">{d.title}</h3>
                  <p className="text-sm text-soft-black">{d.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security & governance */}
      <section className="py-20 lg:py-24 bg-dark-indigo text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-indigo-tint-20 mb-4">
                Security &amp; governance
              </p>
              <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-white mb-6">
                The controls your security team signs off on — built in, not bolted on
              </h2>
              <p className="text-[22px] leading-relaxed text-indigo-tint-20 mb-8">
                Most AI projects die in security review. TurfAI is governed by default: every action
                is authorised, every run is logged immutably, and personal data is tokenised before
                it reaches a model. Deploy inside your own boundary so runtime data never leaves.
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-8">
                <span className="mr-1 text-sm font-semibold text-indigo-tint-20">Certified and compliant</span>
                {["ISO 27001", "ISO 9001", "CMMI Level 3", "HIPAA", "GDPR", "CASA"].map((c) => (
                  <span key={c} className="px-3 py-1 rounded-full text-sm font-medium bg-royal-indigo text-white border border-royal-indigo">
                    {c}
                  </span>
                ))}
              </div>
              <Button variant="onDark" size="lg" className="hover:bg-softblack-tint-20" asChild><PageLink page={"security"} onNavigate={onNavigate}>
                Explore security &amp; trust <ArrowRight className="w-4 h-4 ml-2" />
              </PageLink></Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: EyeOff, title: "Data Shield", desc: "Personal data is tokenised before any prompt reaches a model, then restored after. The model never sees the real values." },
                { icon: KeyRound, title: "RBAC everywhere", desc: "Fine-grained roles and resource ownership, with SSO via SAML 2.0 / OIDC." },
                { icon: ScrollText, title: "Immutable audit log", desc: "Every workflow, agent action, and approval is recorded for compliance and forensics." },
                { icon: Lock, title: "Your boundary", desc: "SaaS, BYOC/VPC, or on-prem. Encrypted in transit (TLS 1.3) and at rest, keys in your KMS." },
              ].map((g) => (
                <div key={g.title} className="rounded-2xl bg-transparent border border-royal-indigo p-6">
                  <div className="w-11 h-11 rounded-xl bg-royal-indigo flex items-center justify-center mb-4">
                    <g.icon className="w-5 h-5 text-indigo-tint-20" />
                  </div>
                  <h3 className="font-semibold text-white mb-1">{g.title}</h3>
                  <p className="text-sm text-indigo-tint-20 leading-relaxed">{g.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        title="TurfAI Platform FAQ"
        subtitle="The essentials, answered plainly"
        accentColor="indigo"
        items={[
          {
            question: "What is TurfAI in one line?",
            answer: "An enterprise platform to design, run, and govern agentic workflows — agents, squads, RAG, and human-in-the-loop on one auditable execution layer.",
          },
          {
            question: "How is this different from building it ourselves?",
            answer: "You'd rebuild the orchestration, RBAC, audit logging, PII protection, and connectors before shipping a single workflow. TurfAI gives you those rails on day one.",
          },
          {
            question: "Which LLMs can it call?",
            answer: "Claude, GPT, Gemini, Llama, Mistral, or a self-hosted model through Ollama. Swap models without rebuilding the workflow.",
          },
          {
            question: "What are the deployment options?",
            answer: "SaaS, your own cloud (BYOC/VPC), on-prem, or edge. The platform is the same; only the boundary changes.",
          },
          {
            question: "How do you protect sensitive data?",
            answer: "Data Shield tokenises personal data before it reaches a model, with an immutable audit log. RBAC and encryption apply throughout.",
          },
          {
            question: "How fast can we go live?",
            answer: "Domain Packs and templates get most teams from first workflow to production in 3–4 weeks.",
          },
        ]}
        maxItems={6}
      />

      {/* Footer CTA band */}
      <section className="py-24 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight mb-6">Move from AI investment to AI outcomes</h2>
          <p className="text-[24px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">
            Tell us the one workflow that's costing you the most. We'll show you how it would run on TurfAI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>
              Schedule a demo
            </Button>
            <Button variant="outlineOnDark" size="lg" onClick={() => (window.location.href = siteConfig.urls.developers)}
            >
              Developer docs <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
